// AuthProvider manages user authentication state and permissions. On logout,
// the React Query cache is cleared to prevent the next login in the same tab
// from reading the previous user's cached data.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import {
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type AuthUser,
} from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import type { User, UserRole, Permissions } from "../types";
// User type is used in the new shape construction below.
import { profileApi } from "../services/api";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: Permissions | null;
  hasArea: (area: string) => boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ADMIN_ROLES: UserRole[] = ["admin", "global-admin"];
const FULL_PERMISSIONS: Permissions = { areas: ["generate", "manage"] };
const DEFAULT_PERMISSIONS: Permissions = { areas: ["generate"] };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const loadUser = useCallback(async () => {
    try {
      const authUser: AuthUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken;
      const payload = idToken?.payload;

      if (payload) {
        const role = ((payload["custom:role"] as string) ||
          (payload.user_role as string) ||
          "viewer") as UserRole;

        const baseUser: User = {
          userId: authUser.userId,
          email: (payload.email as string) || "",
          organisationId:
            (payload["custom:organizationId"] as string) ||
            (payload.org_id as string) ||
            "",
          role,
        };

        // Admin/global-admin always get full permissions (server enforces too)
        // and are implicitly contractAccess: 'all' — no profile call needed.
        if (ADMIN_ROLES.includes(role)) {
          setUser(baseUser);
          setPermissions(FULL_PERMISSIONS);
        } else {
          // Fetch permissions + contractAccess from profile endpoint for
          // non-admin users. The profile response carries the persisted
          // contractAccess scope which the contract-list empty state uses
          // to differentiate "no contracts assigned" from generic empty.
          try {
            const profile = await profileApi.get();
            setUser({
              ...baseUser,
              contractAccess: profile.contractAccess,
              permissions: profile.permissions,
            });
            setPermissions(profile.permissions || DEFAULT_PERMISSIONS);
          } catch {
            setUser(baseUser);
            setPermissions(DEFAULT_PERMISSIONS);
          }
        }
      }
    } catch {
      setUser(null);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // loadUser is an async function; all setState calls inside it run after
    // awaited Cognito/profile operations — not synchronously in this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadUser();
  }, [loadUser]);

  const hasArea = useCallback(
    (area: string): boolean => {
      if (!permissions) return false;
      return permissions.areas.includes(area as Permissions["areas"][number]);
    },
    [permissions],
  );

  const logout = useCallback(async () => {
    await signOut();
    // Purge every cached query so a subsequent same-tab login cannot read the
    // previous user's data (the QueryClient is an app-lifetime singleton).
    queryClient.clear();
    setUser(null);
    setPermissions(null);
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  // Memoise the context value so consumers don't re-render on every provider
  // render from a fresh object identity. The callbacks are already
  // useCallback-stable, so the value changes only when user/permissions/
  // isLoading actually change.
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      permissions,
      hasArea,
      logout,
      refreshUser,
      getAccessToken,
    }),
    [user, isLoading, permissions, hasArea, logout, refreshUser, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
